import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Edit2, Trash2, ExternalLink, RefreshCw } from "lucide-react";
import { useAdmin } from "../components/AdminGate";
import { CATEGORIES, langByCode } from "../lib/blogHelpers";
import { logError } from "../lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlogs() {
  const { headers, signOut } = useAdmin();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blogs`, { headers });
      setBlogs(data || []);
    } catch (e) {
      logError("Failed to load admin blogs:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onDelete = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blogs/${id}`, { headers });
      fetchAll();
    } catch (e) {
      logError("Failed to delete blog:", e);
      window.alert("Could not delete this blog. Please try again.");
    }
  };

  return (
    <div className="container-narrow py-12 sm:py-16" data-testid="admin-blogs-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <p className="eyebrow">Admin · Blogs</p>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 text-[#2C2A28]">
            Your stories.
          </h1>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/admin/blogs/new"
            data-testid="admin-blog-new"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm bg-[#4A6B53] text-[#FAF9F6] hover:bg-[#3A5441] transition-colors text-sm tracking-wide"
          >
            <Plus size={14} />
            New blog
          </Link>
          <button
            onClick={fetchAll}
            className="inline-flex items-center gap-1.5 text-sm text-[#5C5852] hover:text-[#4A6B53] transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
          <button
            onClick={signOut}
            className="text-sm text-[#5C5852] hover:text-[#CC7A52] transition-colors"
          >
            Sign out
          </button>
        </div>
      </div>

      <div className="flex gap-3 mb-6 text-xs text-[#5C5852]">
        <Link to="/admin/comments" className="hover:text-[#4A6B53] underline underline-offset-4">
          Go to Comments dashboard →
        </Link>
      </div>

      {loading && <p className="text-[#5C5852] italic">Loading…</p>}
      {!loading && blogs.length === 0 && (
        <div className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-10 text-center">
          <p className="font-serif text-2xl text-[#2C2A28]">No blogs yet.</p>
          <p className="mt-2 text-sm text-[#5C5852]">
            Click <span className="text-[#4A6B53]">&ldquo;New blog&rdquo;</span> above to write your first post.
          </p>
        </div>
      )}

      <ul className="space-y-4">
        {blogs.map((b) => {
          const langs = Object.keys(b.translations || {});
          const tr =
            b.translations?.[langs.includes("en") ? "en" : langs[0]] || {};
          const cat = CATEGORIES.find((c) => c.code === b.category);
          return (
            <li
              key={b.id}
              className="rounded-sm bg-[#F2EFEB] border border-[#D8D3CC] p-5 sm:p-6"
              data-testid={`admin-blog-${b.id}`}
            >
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    {cat && (
                      <span className="eyebrow !text-[10px] !text-[#CC7A52]">
                        {cat.name}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] tracking-wider uppercase ${
                        b.status === "published"
                          ? "bg-[#4A6B53]/15 text-[#4A6B53]"
                          : "bg-[#D8D3CC] text-[#5C5852]"
                      }`}
                    >
                      {b.status}
                    </span>
                    <span className="text-[#5C5852]">
                      Languages: {langs.map((l) => langByCode(l).native).join(" · ")}
                    </span>
                  </div>
                  <h3 className="mt-2 font-serif text-xl sm:text-2xl text-[#2C2A28]">
                    {tr.title || "Untitled"}
                  </h3>
                  <p className="mt-1 text-xs text-[#5C5852]">
                    Slug: <code className="font-mono">{b.slug}</code>
                    {b.updatedAt &&
                      ` · Updated ${new Date(b.updatedAt).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {b.status === "published" && (
                    <Link
                      to={`/blogs/${b.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      title="View live"
                      className="text-[#5C5852] hover:text-[#4A6B53] transition-colors"
                    >
                      <ExternalLink size={16} />
                    </Link>
                  )}
                  <Link
                    to={`/admin/blogs/${b.id}/edit`}
                    title="Edit"
                    className="text-[#5C5852] hover:text-[#4A6B53] transition-colors"
                    data-testid={`admin-blog-edit-${b.id}`}
                  >
                    <Edit2 size={16} />
                  </Link>
                  <button
                    onClick={() => onDelete(b.id, tr.title || "this post")}
                    title="Delete"
                    className="text-[#5C5852] hover:text-[#CC7A52] transition-colors"
                    data-testid={`admin-blog-delete-${b.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
